package ui

import api.data.DataProperty
import api.data.request.DataUpdateProperty
import api.repository.properties.ImplRepositoryProperties
import api.service.properties.ImplServiceProperties
import api.utils.EnumCurrencies
import dataAdminProfile
import firebaseStorage
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.url.URL
import react.FC
import react.Props
import react.createElement
import react.dom.events.ChangeEventHandler
import react.dom.events.KeyboardEvent
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import react.useState
import routes.EnumRoutes
import ui.components.DashboardIcon
import ui.components.DataSidebarMenu
import ui.components.InquiriesIcon
import ui.components.ListingsIcon
import ui.components.NavBar
import ui.components.OwnerSubmissionsIcon
import ui.components.ScheduleIcon
import web.cssom.ClassName
import web.html.ButtonType
import web.html.HTMLInputElement

external interface PageEditPropertyProps: Props{
    var property: DataProperty
}
// Formatter with commas
fun formatWithCommas(value: String): String {
    return value.replace(Regex("\\B(?=(\\d{3})+(?!\\d))"), ",")
}

val PageEditProperty = FC<PageEditPropertyProps>  {props->
    var isSidebarOpen by useState(false)
    var selectedAmenities by useState(props.property.amenities)
    var isUploading by useState(false)
    var uploadProgress by useState(0)
    val uploadedUrls by useState(ArrayList<String>())
    val dataProperty = DataProperty()
    val dataUpdateProperty = DataUpdateProperty()
    var timestamp by useState <Long>()
    val documentId by useState(props.property.id.toString())
    val currencies by useState(EnumCurrencies.entries.toTypedArray())

    // Form fields state
    var title by useState(props.property.title.toString())
    var location by useState(props.property.location.toString())
    var description by useState(props.property.description.toString())
    var price by useState(props.property.price.toString())
    var currency by useState(props.property.currency.toString())
    var propertyPurpose by useState(props.property.purpose.toString())
    var propertyType by useState(props.property.type.toString())
    var rentFrequency by useState(props.property.rentBillFrequency.toString())
    var displayValue by useState(if (price.isNotEmpty()) formatWithCommas(price) else "")
    var bedrooms by useState(props.property.bedrooms.toString())
    var bathrooms by useState(props.property.bathrooms.toString())
    var images by useState<List<org.w3c.files.File>>(emptyList())
    var imagesFromStorage by useState(props.property.images)
    var email by useState(props.property.ownerEmail.toString())
    var phone by useState(props.property.ownerPhone.toString())
    var firstName by useState(props.property.ownerFirstName.toString())
    var lastName by useState(props.property.ownerLastName.toString())





    // Initialize Firebase Storage
    val storage = firebaseStorage
    val scope = MainScope()

    val currentAmenities = if (propertyType == "House") houseAmenities else landAmenities

    val handleImageChange = { e: kotlinx.html.org.w3c.dom.events.Event ->
        val target = e.target.asDynamic()
        val fileList = target.files
        if (fileList != null) {
            val filesArray = mutableListOf<org.w3c.files.File>()
            for (i in 0 until fileList.length.unsafeCast<Int>()) {
                filesArray.add(fileList[i].unsafeCast<org.w3c.files.File>())
            }
            images = filesArray
        }
    }



    val updateDataProperty = {
        scope.launch {
            dataUpdateProperty.id = documentId
            dataUpdateProperty.images = uploadedUrls
            if (bedrooms.isEmpty()) {
                dataUpdateProperty.bedrooms = 0
            } else {
                dataUpdateProperty.bedrooms = bedrooms.toInt()
            }
            if (bathrooms.isEmpty()) {
                dataUpdateProperty.bathrooms = 0
            } else {
                dataUpdateProperty.bathrooms = bathrooms.toInt()
            }
            dataUpdateProperty.location = location
            dataUpdateProperty.title = title
            dataUpdateProperty.ownerFirstName = firstName
            dataUpdateProperty.ownerLastName = lastName
            dataUpdateProperty.ownerEmail = email
            dataUpdateProperty.ownerPhone = phone
            dataUpdateProperty.price = price.toInt()
            dataUpdateProperty.description = description
            dataUpdateProperty.type = propertyType
            dataUpdateProperty.purpose = propertyPurpose
            dataUpdateProperty.amenities = selectedAmenities!!
            dataUpdateProperty.images = imagesFromStorage!!
            dataUpdateProperty.currency = currency
            dataUpdateProperty.submittedBy = "${dataAdminProfile.firstName} ${dataAdminProfile.lastName}"
            dataUpdateProperty.rentBillFrequency = rentFrequency
            console.log(dataUpdateProperty)
                updateProperty(dataUpdateProperty)
        }
    }

    val uploadImagesToFirebase = { files: List<org.w3c.files.File> ->
        scope.launch {
            try {
                isUploading = true
                timestamp = js("Date.now()").unsafeCast<Long>()

                files.forEachIndexed { index, file ->
                    // Create a unique filename
                    val fileName = "properties/${documentId}/${file.name}"

                    // Get storage reference
                    val storageRef = storage.reference.child(fileName)

                    // Upload file
                    storageRef.putFile(file)

                    // Get download URL
                    val downloadUrl = storageRef.getDownloadUrl()
                    uploadedUrls.add(downloadUrl)

                    // Update progress
                    uploadProgress = ((index + 1) * 100) / files.size
                }
                updateDataProperty()
                window.alert("Images uploaded successfully!")
                isUploading = false
                uploadProgress = 0


                // You can now save these URLs to your database
                // For example: savePropertyListing(uploadedUrls)

            } catch (e: Exception) {
                console.error("Upload failed:", e)
                window.alert("Failed to upload images: ${e.message}")
                isUploading = false
                uploadProgress = 0
            }
        }
    }

    val handleSubmit = {
        if (images.isNotEmpty()) {
            uploadImagesToFirebase(images)

        } else {
            updateDataProperty()
        }
    }

    val handleAmenityToggle = { amenity: String ->
        selectedAmenities = (if (selectedAmenities!!.contains(amenity)) {
            selectedAmenities!!.filter { it != amenity }
        } else {
            selectedAmenities?.plus(amenity) ?: amenity
        }) as ArrayList<String>
    }

    val handlePropertyTypeChange = { type: String ->
        propertyType = type
        selectedAmenities = ArrayList<String>() // Reset amenities when type changes
    }


    div {
        className = ClassName("min-h-screen bg-white flex relative")
        //        Side Navigation
        NavBar{
            this.activeNavItem = "Properties"
        }

        // Main Content
        main {
            className = ClassName("flex-1 p-6 lg:px-48")

            // Page Title
            h1 {
                className = ClassName("text-2xl text-[#7F161B] mb-6")
                +"Edit Listing"
            }

            // Property Details Section
            h2 {
                className = ClassName("text-sm font-normal mb-4")
                +"Property Details"
            }

//            Form
            form {
                // Title
                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Title"
                    }
                    input {
                        id = "title"
                        value = title
                        onChange = { e ->
                            val target = e.target.asDynamic()
                            title = target.value as String
                        }
                        type = web.html.InputType.text
                        required = true
                        placeholder = "Enter Property Title"
                        className =
                            ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                    }
                }
                // Location
                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Location"
                    }
                    input {
                        type = web.html.InputType.text
                        id = "location"
                        value = location
                        onChange = { e ->
                            val target = e.target.asDynamic()
                            location = target.value as String
                        }
                        placeholder = "Enter address or area"
                        required = true
                        className =
                            ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                    }
                }

                // Property Type
                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Property Type"
                    }
                    div {
                        className = ClassName("flex gap-3")
                        button {
                            type = ButtonType.button
                            onClick = {
                                handlePropertyTypeChange("House")
                            }
                            disabled = propertyType == "Land"
                            className = ClassName(
                                "flex-1 px-4 py-3 rounded-lg text-sm font-['Century_Schoolbook'] transition-colors " +
                                        if (propertyType == "House") "bg-[#7F161B] text-white" else "bg-[#F8F8F7] text-[#949494]"
                            )
                            +"House"
                        }
                        button {
                            type = ButtonType.button
                            onClick = {
                                handlePropertyTypeChange("Land")
                            }
                            disabled = propertyType == "House"
                            className = ClassName(
                                "flex-1 px-4 py-3 rounded-lg text-sm font-['Century_Schoolbook'] transition-colors " +
                                        if (propertyType == "Land") "bg-[#7F161B] text-white" else "bg-[#F8F8F7] text-[#949494]"
                            )
                            +"Land"
                        }
                    }
                }

                // Property Purpose
                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Property Purpose"
                    }
                    div {
                        className = ClassName("flex gap-3")
                        button {
                            type = ButtonType.button
                            onClick = { propertyPurpose = "For Sale" }
                            className = ClassName(
                                "flex-1 px-4 py-3 rounded-lg text-sm font-['Century_Schoolbook'] transition-colors " +
                                        if (propertyPurpose == "For Sale") "bg-[#7F161B] text-white" else "bg-[#F8F8F7] text-[#949494]"
                            )
                            +"For Sale"
                        }
                        button {
                            type = ButtonType.button
                            onClick = { propertyPurpose = "For Rent" }
                            className = ClassName(
                                "flex-1 px-4 py-3 rounded-lg text-sm font-['Century_Schoolbook'] transition-colors " +
                                        if (propertyPurpose == "For Rent") "bg-[#7F161B] text-white" else "bg-[#F8F8F7] text-[#949494]"
                            )
                            +"For Rent"
                        }
                    }
                }

                // Amenities Section
                h2 {
                    className = ClassName("text-2xl font-normal mb-4")
                    +"Amenities"
                }

                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Amenities"
                    }
                    div {
                        className = ClassName("bg-[#F8F8F7] rounded-lg p-4")
                        div {
                            className = ClassName("grid grid-cols-2 gap-3")
                            currentAmenities.forEach { amenity ->
                                label {
                                    key = amenity
                                    className = ClassName("flex items-center gap-2 cursor-pointer")
                                    input {
                                        type = web.html.InputType.checkbox
                                        checked = selectedAmenities?.contains(amenity)
                                        onChange = { handleAmenityToggle(amenity) }
                                        className =
                                            ClassName("w-4 h-4 text-[#7F161B] border-gray-300 rounded focus:ring-[#7F161B]")
                                    }
                                    span {
                                        className = ClassName("text-sm font-['Century_Schoolbook']")
                                        +amenity
                                    }
                                }
                            }
                        }
                    }

                    if (selectedAmenities!!.isNotEmpty()) {
                        p {
                            className = ClassName("text-sm text-[#949494] mt-2")
                            +"Selected: ${selectedAmenities?.joinToString(", ")}"
                        }
                    }
                }

                // Description
                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Description"
                    }
                    textarea {
                        placeholder = "Describe your property"
                        value = description
                        onChange = { e ->
                            val target = e.target.asDynamic()
                            description = target.value as String
                        }
                        required = true
                        className =
                            ClassName("w-full h-[151px] bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none resize-none")
                    }
                }

                // Price, Bedrooms, Bathrooms
                div {
                    className = ClassName("flex-1 space-y-1")
                    div {
                        className = ClassName("flex gap-3 mb-6")

                        div {

                            className = ClassName("flex-1 space-y-1")

                            label {
                                className = ClassName("text-sm font-normal")
                                +"Price"
                            }
                            input {
                                type = web.html.InputType.text
                                placeholder = "e.g. 2,500"
                                step = 0.01
                                min = 0.0
                                value = displayValue
                                // Prevent typing "-" and "+"
                                onKeyDown = { e: KeyboardEvent<HTMLInputElement> ->
                                    if (e.key == "-" || e.key == "+") {
                                        e.preventDefault()
                                    }
                                }
                                onChange = { e ->
                                    val target = e.target.value
                                    // Remove non-digit characters
                                    val clean = target.replace(Regex("[^0-9]"), "")
                                    displayValue = if (clean.isNotEmpty()) formatWithCommas(clean) else ""
                                    price = clean
                                }
                                required = true
                                className =
                                    ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                            }
                        }
                        div {

                            className = ClassName("flex-1 space-y-1")

                            label {
                                className = ClassName("text-sm font-normal")
                                +"Currency"
                            }
                            select {
                                onChange = { event ->
                                    currency = event.target.value
                                }

                                currencies.forEach {enumCurrency->
                                    option {
                                        value = enumCurrency.code
                                        +enumCurrency.code
                                    }
                                }
                                className =
                                    ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")

                            }
//                            input {
//                                type = web.html.InputType.number
//                                placeholder = "e.g. 2,500"
//                                value = price
//                                onChange = { e ->
//                                    val target = e.target.asDynamic()
//                                    price = target.value as String
//                                }
//                                required = true
//                                }
                        }
                    }
                    div {
                        className = ClassName("flex gap-3 mb-6")
                        // Only show Bedrooms and Bathrooms for House
                        if (propertyType == "House") {
                            div {
                                className = ClassName("flex-1 space-y-1")
                                label {
                                    className = ClassName("text-sm font-normal")
                                    +"Bedrooms"
                                }
                                input {
                                    type = web.html.InputType.number
                                    placeholder = "e.g. 3"
                                    value = bedrooms
                                    min = 0
                                    // Prevent typing "-" and "+"
                                    onKeyDown = { e: KeyboardEvent<HTMLInputElement> ->
                                        if (e.key == "-" || e.key == "+") {
                                            e.preventDefault()
                                        }
                                    }
                                    onChange = { e ->
                                        val target = e.target.asDynamic()
                                        bedrooms = target.value as String
                                    }
                                    className =
                                        ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                                }
                            }
                            div {
                                className = ClassName("flex-1 space-y-1")
                                label {
                                    className = ClassName("text-sm font-normal")
                                    +"Bathrooms"
                                }
                                input {
                                    type = web.html.InputType.number
                                    placeholder = "e.g. 2"
                                    value = bathrooms
                                    min = 0
                                    // Prevent typing "-" and "+"
                                    onKeyDown = { e: KeyboardEvent<HTMLInputElement> ->
                                        if (e.key == "-" || e.key == "+") {
                                            e.preventDefault()
                                        }
                                    }
                                    onChange = { e ->
                                        val target = e.target.asDynamic()
                                        bathrooms = target.value as String
                                    }
                                    className =
                                        ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                                }
                            }
                        }
                    }
                }
                // Rent Frequency - Only show for "For Rent"
                if (propertyPurpose == "For Rent") {
                    div {
                        className = ClassName("space-y-1 mb-6")
                        label {
                            className = ClassName("text-sm font-normal")
                            +"Rent Billing Period"
                        }
                        div {
                            className = ClassName("flex gap-3")
                            button {
                                type = web.html.ButtonType.button
                                onClick = { rentFrequency = "Per Day" }
                                className = ClassName(
                                    "flex-1 px-4 py-3 rounded-lg text-sm font-['Century_Schoolbook'] transition-colors " +
                                            if (rentFrequency == "Per Day") "bg-[#7F161B] text-white" else "bg-[#F8F8F7] text-[#949494]"
                                )
                                +"Per Day"
                            }
                            button {
                                type = web.html.ButtonType.button
                                onClick = { rentFrequency = "Per Month" }
                                className = ClassName(
                                    "flex-1 px-4 py-3 rounded-lg text-sm font-['Century_Schoolbook'] transition-colors " +
                                            if (rentFrequency == "Per Month") "bg-[#7F161B] text-white" else "bg-[#F8F8F7] text-[#949494]"
                                )
                                +"Per Month"
                            }
                            button {
                                type = web.html.ButtonType.button
                                onClick = { rentFrequency = "Per Year" }
                                className = ClassName(
                                    "flex-1 px-4 py-3 rounded-lg text-sm font-['Century_Schoolbook'] transition-colors " +
                                            if (rentFrequency == "Per Year") "bg-[#7F161B] text-white" else "bg-[#F8F8F7] text-[#949494]"
                                )
                                +"Per Year"
                            }
                        }
                    }
                }
                // Images Section
                h2 {
                    className = ClassName("text-2xl font-normal mb-4")
                    +"Uploaded Images"
                }
                ImageGalleryFromStorage(){
                    this.images = imagesFromStorage?.toList()!!
                }

                h2 {
                    className = ClassName("text-2xl font-normal mb-4")
                    +"Add Images"
                }

                div {
                    className = ClassName("mb-6")
                    label {
                        className = ClassName("block mb-3")

                        div {
                            className = ClassName("flex gap-3 mb-3")
                            if (images.isNotEmpty()) {
                                ImageGallery(){
                                    this.images = images.map {
                                        URL.createObjectURL(it)
                                    }
                                }

                            } else {


                                img {
                                    src =
                                        "https://api.builder.io/api/v1/image/assets/TEMP/8835be0379883a646b1a06aea47b47fd39844d01?width=469"
                                    alt = "Placeholder 1"
                                    className = ClassName("flex-1 h-[145px] object-cover rounded-lg")
                                }
                                img {
                                    src =
                                        "https://api.builder.io/api/v1/image/assets/TEMP/1e044a76fe5c7334b145617afcdef3212eae61ce?width=469"
                                    alt = "Placeholder 2"
                                    className = ClassName("flex-1 h-[145px] object-cover rounded-lg")
                                }
                            }
                        }

                        input {
                            type = web.html.InputType.file
                            multiple = true
                            accept = "image/*"
                            onChange = handleImageChange as ChangeEventHandler<HTMLInputElement>?
                            className =
                                ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-[#7F161B] file:text-white hover:file:bg-[#6a1318]")
                        }
                    }

                    if (images.isNotEmpty()) {
                        p {
                            className = ClassName("text-sm text-[#949494] mt-2")
                            +"${images.size} image(s) selected"
                        }
                    }
                }

                // Owner information
                h2 {
                    className = ClassName("text-2xl font-normal mb-4")
                    +"Owner Information"
                }
                div {
                    className = ClassName("flex-1 space-y-1")
                    div {
                        className = ClassName("flex gap-3 mb-6")

                        div {

                            className = ClassName("flex-1 space-y-1")

                            label {
                                className = ClassName("text-sm font-normal")
                                +"FirstName"
                            }
                            input {
                                type = web.html.InputType.text
                                placeholder = "First Name"
                                value = firstName
                                onChange = { e ->
                                    val target = e.target.asDynamic()
                                    firstName = target.value as String
                                }
                                required = true
                                className =
                                    ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                            }
                        }
                        div {

                            className = ClassName("flex-1 space-y-1")

                            label {
                                className = ClassName("text-sm font-normal")
                                +"Last Name"
                            }
                            input {
                                type = web.html.InputType.text
                                placeholder = "Last Name"
                                value = lastName
                                onChange = { e ->
                                    val target = e.target.asDynamic()
                                    lastName = target.value as String
                                }
                                required = true
                                className =
                                    ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                            }
                        }
                    }
                }
                // Email
                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Email Address"
                    }
                    input {
                        id = "email"
                        value = email
                        onChange = { e ->
                            val target = e.target.asDynamic()
                            email = target.value as String
                        }
                        type = web.html.InputType.text
                        required = true
                        placeholder = "Enter Your Email"
                        className =
                            ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                    }
                }

                // Email
                div {
                    className = ClassName("space-y-1 mb-6")
                    label {
                        className = ClassName("text-sm font-normal")
                        +"Phone Number"
                    }
                    input {
                        id = "phone"
                        value = phone
                        onChange = { e ->
                            val target = e.target.asDynamic()
                            phone = target.value as String
                        }
                        type = web.html.InputType.tel
                        required = true
                        placeholder = "Enter Your Phone Number"
                        className =
                            ClassName("w-full bg-[#F8F8F7] rounded-lg px-4 py-3 text-sm text-[#949494] font-['Century_Schoolbook'] outline-none")
                    }
                }


                // Submit Button
                button {
                    onClick = {

                        if (propertyType == "House"){
                            if (checkHouseInputs(
                                    propertyType,
                                    propertyPurpose,
                                    title,
                                    location,
                                    description,
                                    price,
                                    bedrooms,
                                    bathrooms,
                                    rentFrequency,
                                    firstName,
                                    lastName,
                                    email,
                                    phone
                                )){
                                handleSubmit()
                            } else {
                                window.alert("Fill In all values")
                            }
                        }
                        if (propertyType == "Land"){
                            if (checkLandInputs(
                                    propertyType,
                                    propertyPurpose,
                                    title,
                                    location,
                                    description,
                                    price,
                                    rentFrequency,
                                    firstName,
                                    lastName,
                                    email,
                                    phone
                                )){
                                handleSubmit()
                            } else {
                                window.alert("Fill In all values")
                            }
                        }
                    }
                    type = ButtonType.button
                    disabled = isUploading
                    className = ClassName(
                        "bg-[#7F161B] text-[#FFF4F4] rounded-lg px-4 py-3 text-sm font-['Century_Schoolbook'] " +
                                if (isUploading) "opacity-50 cursor-not-allowed" else ""
                    )
                    +if (isUploading) "Uploading... $uploadProgress%" else "Update Property"
                }
            }
        }
    }
}


val ImageGalleryFromStorage = FC<ImagesProps> { props ->
    var selectedIndex by useState<Int?>(null)
    var images by useState(props.images.toMutableList())
    val totalImages = props.images.size

    fun showNext() {
        selectedIndex = if (selectedIndex != null) (selectedIndex!! + 1) % totalImages else 0
    }

    fun showPrev() {
        selectedIndex = if (selectedIndex != null) (selectedIndex!! - 1 + totalImages) % totalImages else totalImages - 1
    }


    fun deleteImage(index: Int) {
        images = images.toMutableList().apply { removeAt(index) }
    }

    div {
        className = ClassName("flex flex-row gap-4 overflow-x-auto whitespace-nowrap mb-8")
        images.forEachIndexed { index, image ->
            div {
                key = index.toString()
                className = ClassName("relative inline-block flex-shrink-0")

                // Image
                img {
                    src = image
                    alt = "Image $index"
                    className = ClassName("h-36 w-auto object-cover rounded-xl cursor-pointer")
                }

                // Delete button overlay
                button {
                    type = ButtonType.button
                    className = ClassName(
                        "absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm"
                    )
                    +"×"
                    onClick = {
                        deleteImage(index)
                    }
                }
            }
        }
    }
    // Popup modal with buttons
    if (selectedIndex != null) {
        div {
            className = ClassName("fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50")

            // Close on background click
            onClick = { selectedIndex = null }

            // Previous button
            button {
                type = ButtonType.button
                className = ClassName(
                    "absolute left-4 top-1/2 transform -translate-y-1/2 bg-white text-black rounded-full w-10 h-10 flex items-center justify-center"
                )
                +"‹"
                onClick = {
                    it.stopPropagation() // Prevent closing modal
                    showPrev()
                }
            }

            // Next button
            button {
                type = ButtonType.button
                className = ClassName(
                    "absolute right-4 top-1/2 transform -translate-y-1/2 bg-white text-black rounded-full w-10 h-10 flex items-center justify-center"
                )
                +"›"
                onClick = {
                    it.stopPropagation() // Prevent closing modal
                    showNext()
                }
            }

            // Display selected image
            img {
                src = props.images[selectedIndex!!]
                alt = "Selected Image"
                className = ClassName("max-h-[90%] max-w-[90%] rounded-lg")
            }

            // Close button
            button {
                type = ButtonType.button
                className = ClassName("absolute top-4 right-4 text-white text-2xl font-bold")
                +"×"
                onClick = { selectedIndex = null }
            }
        }
    }
}

suspend fun updateProperty(dataUpdateProperty: DataUpdateProperty){
    console.log(dataUpdateProperty)
    val implAddProperty = ImplRepositoryProperties(ImplServiceProperties).updateProperty(dataUpdateProperty)
    if (implAddProperty){
        window.location.href = "${EnumRoutes.PropertyListing.routeID}?id=${dataUpdateProperty.id}"
    }
}

